import React, { memo, useState, useCallback, useMemo } from 'react';
import { YStack, Button } from 'tamagui';

import { CobuText } from '@cobu/ui';
import AutoLinkText from './AutoLinkText';
import { Strings } from '../../../constants';

interface ShowMoreTextProps {
  /** The text content to display */
  text: string;
  /** Maximum number of characters to show initially (default: 300) */
  maxLength?: number;
  /** Maximum number of lines to show initially (default: 4) */
  maxLines?: number;
  /** Text style variant */
  variant?: 'body' | 'caption' | 'heading';
  /** Font size */
  fontSize?: any;
  /** Font weight */
  fontWeight?: any;
  /** Text color */
  color?: any;
  /** Line height */
  lineHeight?: number;
  /** Animation duration for expand/collapse */
  animationDuration?: number;
}

const ShowMoreText: React.FC<ShowMoreTextProps> = memo(({
  text,
  maxLength = 300,
  maxLines = 4,
  variant = 'body',
  fontSize = '$3',
  fontWeight = 'normal',
  color = '$color',
  lineHeight = 20,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const strings = Strings.get().discussion;

  // Determine if text needs truncation
  const { shouldTruncate, displayText } = useMemo(() => {
    if (!text) {
      return {
        shouldTruncate: false,
        truncatedText: '',
        displayText: '',
      };
    }

    // Check if text exceeds character limit
    const exceedsCharLimit = text.length > maxLength;

    // Count approximate lines (rough estimation)
    const wordsPerLine = 8; // Average words per line
    const wordCount = text.split(' ').length;
    const estimatedLines = Math.ceil(wordCount / wordsPerLine);
    const exceedsLineLimit = estimatedLines > maxLines;

    const shouldTruncate = exceedsCharLimit || exceedsLineLimit;

    if (!shouldTruncate) {
      return {
        shouldTruncate: false,
        truncatedText: text,
        displayText: text,
      };
    }

    // Truncate at character limit, but try to break at word boundary
    let truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');

    if (lastSpaceIndex > maxLength * 0.8) {
      // If we can break at a word boundary within 80% of max length, do so
      truncated = truncated.substring(0, lastSpaceIndex);
    }

    return {
      shouldTruncate: true,
      truncatedText: truncated,
      displayText: isExpanded ? text : truncated,
    };
  }, [text, maxLength, maxLines, isExpanded]);

  // Toggle expand/collapse
  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  // Don't render if no text
  if (!text) return null;

  return (
    <YStack>
      <AutoLinkText
        text={displayText}
        variant={variant}
        fontSize={fontSize}
        fontWeight={fontWeight}
        color={color}
        lineHeight={lineHeight}
        appendEllipsis={shouldTruncate && !isExpanded}
      />

      {/* Show more/less button */}
      {shouldTruncate && (
        <Button
          unstyled
          onPress={toggleExpanded}
          als="flex-start"
          mt="$1"
          py="$1"
          pressStyle={{ o: 0.7 }}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <CobuText
            variant="caption"
            color="$textLinkColor"
            fontSize="$1"
            fontWeight="bold"
          >
            {isExpanded ? strings.buttons.showLess : strings.buttons.showMore}
          </CobuText>
        </Button>
      )}
    </YStack>
  );
});

ShowMoreText.displayName = 'ShowMoreText';

export default ShowMoreText; 